// src/components/results/MistakeCard.jsx
//
// One question, corrected: the student's answer struck through in red pen, the right
// answer, the trap or reason behind the mistake, the deciding quote and a next step.
// Used by the answer sheet's detail panel and by "Review my mistakes".
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import ExplanationPanel from '../practice/ExplanationPanel';
import { questionPeer } from './peerText';
import { explainCause, familyEntry, labelOf, pick, practiceLinks, questionText } from './storyText';

const HAND = { fontFamily: "'Caveat', cursive" };

export default function MistakeCard({ item, q, part, where, idx, exam, formatAnswer, isFree, onPractice, onLocked, note, norms }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const wrong = !item.correct;
  const cause = wrong ? explainCause(item, idx, lang, t) : null;
  const shown = questionText(q, part) ?? t('examResults.story.whichHeading', { n: q.questionNumber });
  // Practice: the grammar point if there is one, otherwise the skill.
  const practiceEntry = item.point ? idx.point[item.point] : familyEntry(idx, 'skill', item.skill);
  const links = wrong ? practiceLinks(practiceEntry, exam.level, exam.examProvider, t, item.section) : [];
  const peer = questionPeer(norms, exam.examFileName, item);
  const skillLabel = item.point ? pick(idx.point[item.point]?.label, lang) : labelOf(idx, 'skill', item.skill, lang);

  return (
    <article className="rs-paper rs-up" style={{ borderTop: `6px solid ${wrong ? '#DC2626' : '#006D77'}` }}>
      <div className="flex flex-wrap gap-x-7 gap-y-4 px-5 sm:px-6 pt-5 pb-6">
        <div className="flex-[999_1_420px] min-w-0 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-stone-600">{where}</span>
            {item.confident && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-stone-900 text-white">{t('examResults.story.youWereSure')}</span>
            )}
            {item.flagged && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-200 text-amber-900">{t('examResults.story.youFlagged')}</span>
            )}
          </div>
          {shown && <p className="m-0 text-lg font-semibold leading-snug text-stone-900">{shown}</p>}

          {wrong ? (
            <>
              <div className="flex flex-wrap gap-x-7 gap-y-2 text-[15px]">
                <span className="inline-flex items-center gap-2">
                  {t('examResults.yourAnswer')}
                  <span className="relative font-semibold text-red-800 px-0.5">
                    {item.answered ? formatAnswer(item.answer, q, part) : t('examResults.noAnswer')}
                    {item.answered && (
                      <svg viewBox="0 0 100 20" preserveAspectRatio="none" className="absolute -left-1 -right-1 top-0 w-[calc(100%+8px)] h-full overflow-visible" aria-hidden="true">
                        <path className="rs-stroke rs-draw" pathLength="1" d="M2 13 C 30 7, 70 9, 98 6" stroke="#DC2626" strokeWidth="2.6" style={{ animationDelay: '.3s' }} />
                      </svg>
                    )}
                  </span>
                </span>
                <span>{t('examResults.correctAnswer')} <strong className="text-[#006D77]">{formatAnswer(q.correctAnswer, q, part)}</strong></span>
              </div>
              <div className="flex flex-wrap gap-2">
                {cause.label && <span className="text-[13px] font-bold px-2.5 py-1.5 rounded-full bg-red-100 text-red-800">{cause.label}</span>}
                {skillLabel && <span className="text-[13px] font-semibold px-2.5 py-1.5 rounded-full bg-stone-100 text-stone-700">{skillLabel}</span>}
              </div>
              {cause.why && <p className="m-0 text-base leading-relaxed text-stone-900">{cause.why}</p>}
              {peer && <p className="m-0 text-sm font-semibold text-[#006D77]">{t(peer.key, { pct: peer.pct })}</p>}
              {item.evidence && (
                <p className="m-0 text-[15px] leading-relaxed text-stone-700">
                  {t('examResults.story.textSays')}{' '}
                  <span className="italic underline decoration-red-600 decoration-2 underline-offset-4">„{item.evidence}“</span>
                </p>
              )}
              {cause.tip && <p className="m-0 text-sm text-stone-700"><strong>{t('examResults.story.tip')}</strong> {cause.tip}</p>}
              {q.explanation && <ExplanationPanel explanation={q.explanation} />}
              {links.length > 0 && (
                <div className="flex flex-wrap gap-2.5 mt-1">
                  {links.map((l) => {
                    const locked = l.premium && isFree;
                    return (
                      <button
                        key={l.label}
                        type="button"
                        onClick={() => (locked ? onLocked() : onPractice(l))}
                        className={`rs-focus min-h-[44px] px-4 rounded-[10px] text-sm font-semibold inline-flex items-center gap-2 touch-manipulation active:scale-[0.98] transition-transform ${l.premium ? 'bg-stone-900 text-white' : 'border-2 border-stone-900 text-stone-900 bg-white'}`}
                      >
                        {locked && <Lock className="w-4 h-4" aria-hidden="true" />}
                        {l.label}{locked ? ` · ${t('examResults.story.premium')}` : ''}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="m-0 text-base font-semibold text-[#006D77]">
              {item.flagged ? t('examResults.story.flaggedRight') : t('examResults.story.right')}
            </p>
          )}
        </div>
        {note && (
          <div className="flex-[1_1_150px] flex items-start justify-center pt-2">
            <span className="redpen-note text-[38px] leading-none -rotate-[4deg]" style={{ ...HAND, color: wrong ? '#DC2626' : '#006D77', animationDelay: '.6s' }}>{note}</span>
          </div>
        )}
      </div>
    </article>
  );
}
